import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import colors from '../../../constants/colors.constants';
import Typo from '../../../components/atom/Typo';
import { fetchProductFilters, fetchProducts } from '../../../apis/products';
import { useQuery } from 'react-query';
import useAuthStore from '../../../store/useAuthStore';
import ProductItem from './components/ProductItem';
import Button from '../../../components/atom/Button';
import strings from '../../../constants/strings.constants';
import useProductOptionsStore from '../../../store/useProductOptionsStore';
import { useSelectedProductOptionsSync } from '../../../shared/hooks/useSelectedProductOptionsValidation';
import useLanguageCodeStore, {
  LanguageCode,
} from '../../../store/useLanguageCodeStore';
import styled from 'styled-components';
import { Collapse } from 'react-collapse';
import { ReactComponent as ExpandToggleIcon } from '../../../assets/icon-arrow-menu-toggle.svg';
import { ReactComponent as InfoIcon } from '../../../assets/svgs/icon-info.svg';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import {
  formatCurrency,
  getExhibitionProductFilter,
  getProductOptionTotalPrice,
  getRecommendProductFilter,
  getSelectedExhibitionProductFilter,
} from '../../../utils/utils';
import SelectedProductOptionItem from './components/SelectedProductOptionItem';
import DateSelectModal from './components/DateSelectModal';
import { Fade } from 'react-awesome-reveal';
import Skeleton from 'react-loading-skeleton';
import FilterBottomSheet from './components/FilterBottomSheet';
import { ReactComponent as ArrowDownIcon } from '../../../assets/svgs/arrow-down.svg';
import { ReactComponent as IconBack } from '../../../assets/arrow-back.svg';
import { ReactComponent as IconSearch } from '../../../assets/search.svg';
import { ProductFilter } from '../../../apis/models/products/res/ProductFilter';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTER_PATHS } from '../../../routes/routes.constatns';
import Input from '../../../components/atom/Input';
import { fetchExhibitions } from '../../../apis/exhibitions';
import moment from 'moment';
import ExhibitionDetailModal from '../ExhibitionDetailPage/ExhibitionDetailPage';
import { Tutorial } from '../Tutorial/Tutorial';
import URL_QUERY_KEYS from '../../../constants/urlQueryKeys.constants';
import {
  sendBeginCheckoutEvent,
  sendRemoveFromCartEvent,
  sendViewPromotionEvent,
} from '../../../utils/ga-utils';

const CustomDot = ({ ...rest }) => {
  const { active } = rest;
  return (
    <button
      className="mx-[2px]"
      style={{
        width: 6,
        height: 6,
        borderRadius: 3,
        backgroundColor: active ? '#64748B' : '#CBD5E1',
      }}
    />
  );
};

function TreatmentReservationPage() {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchParams] = useSearchParams();
  const isEventMode = !!searchParams.get('event');
  const openExhibitionDetail = !!searchParams.get('openExhibitionDetail');
  const getInitialExhibitionId = () => {
    try {
      const exhibitionId = searchParams.get('exhibitionDetailId');
      if (exhibitionId) {
        return Number(exhibitionId);
      }
    } catch (e) {
      console.warn(e);
      return;
    }
  };
  const getInitialFilter = useCallback(() => {
    try {
      const productIds = searchParams.get('productIds');
      const exhibitionId = searchParams.get('exhibitionId');
      const selectedExhibitionId = searchParams.get('selectedExhibitionId');
      const eventId = searchParams.get('eventId');

      if (productIds) {
        const recommendProductIds = JSON.parse(productIds) as number[];
        if (recommendProductIds.length > 0) {
          return getRecommendProductFilter(recommendProductIds);
        }
      } else if (exhibitionId) {
        // Secret 기획전용
        return getExhibitionProductFilter(
          Number(exhibitionId),
          Number(eventId || 0),
        );
      } else if (selectedExhibitionId) {
        // 기획전 필터용
        return getSelectedExhibitionProductFilter(
          Number(selectedExhibitionId),
          Number(eventId || 0),
        );
      }
      return undefined;
    } catch (e) {
      return undefined;
    }
  }, [searchParams]);

  const [exhibitionDetailModalVisible, setExhibitionDetailModalVisible] =
    useState(openExhibitionDetail);
  const [exhibitionId, setExhibitionId] = useState<number | undefined>(
    getInitialExhibitionId(),
  );

  const { partner } = useAuthStore();
  const { productOptions, remove } = useProductOptionsStore();
  useSelectedProductOptionsSync();
  const languageCode = useLanguageCodeStore((state) => state.languageCode);
  const [footerExpanded, setFooterExpanded] = useState(true);
  const [dateSelectModalVisible, setDateSelectModalVisible] = useState(false);
  const [filterBottomSheetVisible, setFilterBottomSheetVisible] =
    useState(false);
  const [selectedFilter, setSelectedFilter] = useState<
    ProductFilter | undefined
  >(getInitialFilter());
  const location = useLocation();

  const [noData, setNoData] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');

  const isIncludeVAT = useMemo(() => {
    return (
      partner?.globalHomepageConfig?.isIncludeVat ||
      partner?.homepageConfig?.isIncludeVat
    );
  }, [partner]);

  const {
    data: exhibitions = [],
    isSuccess: isSuccessExhibitions,
    isLoading: isLoadingExhibitions,
  } = useQuery({
    queryKey: `FETCH_EXHIBITIONS_${partner?.id}`,
    queryFn: async () => {
      let isTempData: boolean | undefined;
      if (searchParams.get(URL_QUERY_KEYS.IS_TEMP_DATA) === 'true') {
        isTempData = true;
      }
      return fetchExhibitions(partner?.partnerCode || '', isTempData);
    },
    retry: false,
    enabled: !!partner,
    refetchOnWindowFocus: false,
  });

  const shouldDisplayExhibitions =
    !!partner?.chatBotConfig?.useMimoWebExhibition &&
    !searchMode &&
    !searchParams.get('productIds');

  const { data: products = [], isLoading } = useQuery({
    queryKey: `FETCH_PRODUCTS_${JSON.stringify(selectedFilter || {})}_${searchMode}_${languageCode}_${partner?.id}`,
    queryFn: async () => {
      let eventId: number | undefined;
      let categoryId: number | undefined;
      let exhibitionId: number | undefined;
      let selectedExhibitionId: number | undefined;

      if (!searchMode && selectedFilter && !selectedFilter.isRecommendProduct) {
        const { isEvent, id } = selectedFilter;
        if (isEvent) {
          eventId = id;
          categoryId = undefined;
        } else {
          eventId = undefined;
          categoryId = id;
        }
      }

      if (selectedFilter?.exhibitionId) {
        exhibitionId = selectedFilter.exhibitionId;
        eventId = selectedFilter.id;
      }

      if (selectedFilter?.selectedExhibitionId) {
        selectedExhibitionId = selectedFilter.selectedExhibitionId;
        eventId = selectedFilter.id;
      }

      let isTempData: boolean | undefined;
      if (searchParams.get(URL_QUERY_KEYS.IS_TEMP_DATA) === 'true') {
        isTempData = true;
      }

      return fetchProducts({
        eventId: eventId || undefined,
        categoryId: categoryId || undefined,
        partnerCode: partner?.partnerCode || '',
        exhibitionId,
        selectedExhibitionId,
        languageCode:
          languageCode !== LanguageCode.ko ? languageCode : undefined,
        isTempData,
      });
    },
    retry: false,
    enabled: !!partner,
    refetchOnWindowFocus: false,
    select: (data) => {
      if (isEventMode) {
        return data.filter((product) => !!product.event);
      } else {
        return data;
      }
    },
    onSuccess: () => {
      setNoData(false);
    },
    onError: () => {
      setNoData(true);
    },
  });

  const { data: filters = [], isLoading: isLoadingFilters } = useQuery({
    queryKey: `FETCH_FILTERS_${languageCode}_${partner?.id}`,
    queryFn: async () => {
      let isTempData: boolean | undefined;
      if (searchParams.get(URL_QUERY_KEYS.IS_TEMP_DATA) === 'true') {
        isTempData = true;
      }
      return fetchProductFilters(
        partner?.partnerCode || '',
        languageCode !== LanguageCode.ko ? languageCode : undefined,
        isTempData,
      );
    },
    retry: false,
    select: (data) => {
      if (isEventMode) {
        return data.filter((filter) => !!filter.event);
      } else {
        return data;
      }
    },
    onSuccess: () => {
      if (!getInitialFilter()) {
        if (!shouldDisplayExhibitions) {
          setFilterBottomSheetVisible(true);
        }
      }
    },
    enabled: !!partner,
    refetchOnWindowFocus: false,
  });

  const navigate = useNavigate();

  const toggleSearchMode = (searchMode: boolean) => {
    setSearchMode(searchMode);
    setSearchKeyword('');
  };

  const renderSelectedProductOptions = () => {
    return (
      <div
        className="flex flex-col gap-4"
        style={{
          borderTop: `1px solid ${colors.defaultBorder}`,
          padding: '12px 0',
          maxHeight: '40vh',
          overflowY: 'auto',
        }}
      >
        {productOptions.map((productOption) => (
          <SelectedProductOptionItem
            key={productOption.id}
            productOption={productOption}
            onDeleteClick={({ id }) => {
              remove(id);
              sendRemoveFromCartEvent([productOption]);
            }}
          />
        ))}
      </div>
    );
  };

  const getHeaderTitle = useCallback(() => {
    if (selectedFilter) {
      return selectedFilter.name;
    }

    return strings.allTreatment;
  }, [selectedFilter]);

  const getFilteredProducts = useCallback(() => {
    if (searchMode) {
      if (searchKeyword) {
        return products.filter(
          ({ productName, categoryName, event, productOptions = [] }) => {
            // 상품명
            if (productName.includes(searchKeyword)) return true;

            // 카테고리명
            let categoryText;
            if (categoryName) {
              categoryText = categoryName;
            } else if (event) {
              const { eventTitle } = event;
              categoryText = eventTitle;
            }

            if (categoryText && categoryText.includes(searchKeyword))
              return true;

            // 상품 옵션
            const found = productOptions.find(({ productOptionName }) => {
              return productOptionName.includes(searchKeyword);
            });
            return !!found;
          },
        );
      }

      return products;
    } else {
      if (selectedFilter && selectedFilter.isRecommendProduct) {
        const filteredProducts = products.filter(({ id }) => {
          return selectedFilter.recommendProductIds?.includes(id);
        });

        if (filteredProducts.length > 0) {
          return filteredProducts;
        } else {
          // 미존재 상품의 경우 전체로 클리어
          setSelectedFilter(undefined);
        }
      }

      return products;
    }
  }, [products, selectedFilter, searchKeyword, searchMode]);

  const focusSearchInput = useCallback((node: HTMLInputElement) => {
    node?.focus();

    if (node) {
      // @ts-ignore
      searchInputRef.current = node;
    }
  }, []);

  useEffect(() => {
    if (!shouldDisplayExhibitions) {
      return;
    }
    if (!isSuccessExhibitions) {
      return;
    }
    if (exhibitions?.length <= 0) {
      return;
    }

    sendViewPromotionEvent({
      promotionId: exhibitions[0].id,
      promotionName: exhibitions[0].title,
    });
  }, [exhibitions, isSuccessExhibitions, shouldDisplayExhibitions]);

  return (
    <div className="h-full flex flex-col">
      <Tutorial />
      {/*  Header */}
      <div
        className="min-h-[48px] flex items-center justify-center px-[16] relative"
        style={{
          borderBottom: `1px solid ${colors.defaultBorder}`,
        }}
      >
        <div
          style={{
            padding: '5px',
            cursor: 'pointer',
            position: 'absolute',
            left: 16,
          }}
        >
          <IconBack
            onClick={() => {
              if (searchMode) {
                toggleSearchMode(false);
              } else {
                navigate(
                  {
                    pathname: ROUTER_PATHS.RESERVATION,
                    search: location.search,
                  },
                  {
                    replace: true,
                  },
                );
              }
            }}
          />
        </div>
        {searchMode && !selectedFilter?.exhibitionId && (
          <div
            style={{
              width: '100%',
              marginLeft: 48,
            }}
          >
            <Input
              style={{
                width: '100%',
              }}
              value={searchKeyword}
              onChange={(value) => {
                setSearchKeyword(value);
              }}
              borderless
              ref={focusSearchInput}
              placeholder={strings.searchPlaceholder}
            />
          </div>
        )}
        {!searchMode && (
          <CategorySelectButton
            onClick={() => {
              if (!selectedFilter?.exhibitionId) {
                setFilterBottomSheetVisible(true);
              }
            }}
          >
            <Typo type="b-16">{getHeaderTitle()}</Typo>
            {!selectedFilter?.exhibitionId ? (
              <ArrowDownIcon
                color={colors.subTitle}
                width={11}
                height={11}
                style={{
                  marginBottom: 4,
                }}
              />
            ) : null}
          </CategorySelectButton>
        )}

        {!searchMode && !selectedFilter?.exhibitionId && (
          <div
            style={{
              padding: '5px',
              cursor: 'pointer',
              position: 'absolute',
              right: 16,
            }}
          >
            <IconSearch
              onClick={() => {
                toggleSearchMode(true);
              }}
            />
          </div>
        )}
      </div>

      {/*  Content */}
      <div className="h-full flex-1 overflow-y-auto py-[16px]">
        <Fade duration={200}>
          {shouldDisplayExhibitions && exhibitions?.length > 0 && (
            <div className="mb-8 relative pb-8 px-[16px]">
              <Carousel
                swipeable
                afterChange={(index, state) => {
                  const actualIndex =
                    exhibitions.length > 0
                      ? ((state.currentSlide % exhibitions.length) +
                          exhibitions.length) %
                        exhibitions.length
                      : 0;
                  const exhibition = exhibitions[actualIndex];
                  if (!exhibition) {
                    return;
                  }
                  sendViewPromotionEvent({
                    promotionId: exhibition.id,
                    promotionName: exhibition.title,
                  });
                }}
                draggable={false}
                showDots={true}
                customDot={<CustomDot />}
                responsive={{
                  desktop: {
                    breakpoint: { max: 3000, min: 1024 },
                    items: 1,
                  },
                  tablet: {
                    breakpoint: { max: 1024, min: 464 },
                    items: 1,
                  },
                  mobile: {
                    breakpoint: { max: 464, min: 0 },
                    items: 1,
                  },
                }}
                infinite={true}
                renderDotsOutside
                customTransition="all .5"
                transitionDuration={500}
                containerClass="carousel-container"
                removeArrowOnDeviceType={['mobile']}
                dotListClass="custom-dot-list-style"
                itemClass="carousel-item-padding-40-px"
              >
                {exhibitions.map((item) => {
                  let eventTermText = '상시 이벤트';
                  const { startedAt, endedAt } = item;
                  let startDateText = '';
                  let endDateText = '';
                  if (startedAt) {
                    startDateText = `${moment(startedAt).format('YY.MM.DD')}`;
                  }
                  if (endedAt) {
                    endDateText = `${moment(endedAt).format('YY.MM.DD')} 까지`;
                  }

                  if (startDateText && endDateText) {
                    eventTermText = `${startDateText} ~ ${endDateText}`;
                  } else if (endDateText) {
                    eventTermText = `${endDateText} 까지`;
                  }

                  return (
                    <div
                      key={item.id}
                      className="px-4 cursor-pointer"
                      onClick={() => {
                        setExhibitionId(item.id);
                        setExhibitionDetailModalVisible(true);
                      }}
                    >
                      <div className="flex flex-col items-center mb-3 gap-1">
                        <Typo
                          style={{
                            fontSize: 20,
                          }}
                          type="b-18"
                        >
                          {item.title}
                        </Typo>
                        {item.description && (
                          <div>
                            <Typo type="r-14" color={colors.slate500}>
                              {item.description}
                            </Typo>
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          borderRadius: 14,
                          overflow: 'hidden',
                        }}
                      >
                        {item.thumbnailUrl && (
                          <img src={item.thumbnailUrl} alt={item.title} />
                        )}
                        <div className="flex items-center justify-between px-3 py-2 bg-[#F1F5F9]">
                          <Typo type="r-12" color={colors.slate500}>
                            이벤트 기간
                          </Typo>

                          <Typo type="r-12" color={colors.slate500}>
                            {eventTermText}
                          </Typo>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </Carousel>
            </div>
          )}

          {/* Divider */}
          {shouldDisplayExhibitions && <div className="h-2 bg-[#F1F5F9] " />}
          <div className="flex flex-col gap-3 bg-white px-[16px]">
            {shouldDisplayExhibitions && (
              <div>
                <div className="mt-6 mb-1">
                  <Typo type="m-16">{getHeaderTitle()}</Typo>
                </div>
              </div>
            )}

            {!(isLoading || isLoadingFilters) && noData && (
              <Typo type="r-16" color={colors.subTitle}>
                {strings.noProducts}
              </Typo>
            )}
            {isLoading || isLoadingFilters ? (
              <Skeleton
                style={{
                  height: 100,
                  marginBottom: 12,
                  lineHeight: 1.5,
                  borderRadius: 16,
                }}
                count={5}
              />
            ) : (
              getFilteredProducts().map((product) => (
                <ProductItem
                  searchKeyword={searchKeyword}
                  key={product.id}
                  product={product}
                  exhibitionId={selectedFilter?.exhibitionId}
                />
              ))
            )}
          </div>
        </Fade>
      </div>
      {/*  Footer */}
      <Footer className="shrink-0">
        <div>
          <div
            className="flex justify-between"
            style={{
              cursor: 'pointer',
              paddingBottom: 12,
            }}
            onClick={() => {
              setFooterExpanded(!footerExpanded);
            }}
          >
            <div className="flex gap-1 items-center">
              <Typo type="m-14">{strings.expectedPrice}</Typo>
              <Typo type="m-12" color={colors.subTitle2}>
                {isIncludeVAT ? strings.includeVAT : strings.excludeVAT}
              </Typo>
            </div>

            <div className="flex items-center gap-2">
              <Typo type="b-18">
                {formatCurrency(getProductOptionTotalPrice(productOptions))}
                {strings.priceUnit}
              </Typo>
              <ToggleIconWrapper $open={!footerExpanded}>
                <ExpandToggleIcon
                  style={{
                    width: 14,
                    height: 14,
                    color: '#8C939A',
                  }}
                />
              </ToggleIconWrapper>
            </div>
          </div>

          <Collapse isOpened={footerExpanded}>
            {productOptions.length > 0 && renderSelectedProductOptions()}
          </Collapse>
        </div>
        <Button
          style={{
            width: '100%',
            height: '72px',
          }}
          disabled={productOptions.length === 0}
          onClick={() => {
            setDateSelectModalVisible(true);
            sendBeginCheckoutEvent(productOptions);
          }}
        >
          <div className="flex flex-col gap-[4px]">
            <div className="justify-center flex items-center gap-2">
              <Typo
                style={{
                  color:
                    productOptions.length > 0
                      ? 'white'
                      : colors.btnDisabledText,
                }}
                type="m-16"
              >
                {strings.makeReservation}
              </Typo>
              {productOptions.length > 0 && (
                <span
                  className="rounded"
                  style={{
                    color: colors.primary3,
                    background: 'white',
                    padding: 3,
                    minWidth: 24,
                    borderRadius: 12,
                    fontSize: 15,
                    fontWeight: 500,
                  }}
                >
                  {productOptions.length}
                </span>
              )}
            </div>

            <Typo
              type="r-14"
              style={{
                fontSize: 13,
                color:
                  productOptions.length > 0
                    ? '#BFDBFE'
                    : colors.btnDisabledText,
              }}
            >
              <div className="flex gap-1 justify-center">
                <InfoIcon
                  color={
                    productOptions.length > 0
                      ? '#BFDBFE'
                      : colors.btnDisabledText
                  }
                />
                {strings.productPriceNoticeDesc}
              </div>
            </Typo>
          </div>
        </Button>
      </Footer>
      <DateSelectModal
        visible={dateSelectModalVisible}
        onCancel={() => {
          setDateSelectModalVisible(false);
        }}
      />
      <FilterBottomSheet
        selectedFilter={selectedFilter}
        filters={filters}
        visible={filterBottomSheetVisible}
        onCancel={() => {
          setFilterBottomSheetVisible(false);
        }}
        onFilterSelected={(filter) => {
          setSelectedFilter(filter);
        }}
      />
      <ExhibitionDetailModal
        exhibitionId={exhibitionId}
        visible={exhibitionDetailModalVisible}
        onCancel={() => {
          setExhibitionDetailModalVisible(false);
        }}
        onSelected={() => {
          setExhibitionDetailModalVisible(false);
          setDateSelectModalVisible(true);
          sendBeginCheckoutEvent(productOptions);
        }}
      />
    </div>
  );
}

export default TreatmentReservationPage;

const Footer = styled.div`
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  padding: 16px 20px 32px 20px;
  position: relative;
  box-shadow: 0px -4px 30px 0px #0000001f;
`;

const ToggleIconWrapper = styled.div<{ $open: boolean }>`
  & > svg {
    transition: all 0.2s;
    transform: ${(props) => {
      if (props.$open) return 'rotate(180deg)';
      return 'rotate(0)';
    }};
  }
`;

const CategorySelectButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
`;
